import React, { useState } from 'react';
import { Home, History, User, Play, ChevronRight, ChevronLeft, Flame, Trophy, Bug } from 'lucide-react';
import { MissedWorkoutModal } from '../components/MissedWorkoutModal';
import { ChangeTargetDateModal } from '../components/ChangeTargetDateModal';
import { SkipWorkoutModal } from '../components/SkipWorkoutModal';
import { CompletedProgramModal } from '../components/CompletedProgramModal';
import { RebuildProgramSheet } from '../components/RebuildProgramSheet';
import { PostWorkoutFeedbackSheet } from '../components/PostWorkoutFeedbackSheet';
import { RecoveryFeedbackSheet } from '../components/RecoveryFeedbackSheet';
type CalendarView = 'week' | 'month';
type DashboardScenario = 'default' | 'keep-date' | 'align-monday';
interface DashboardProps {
  onStartWorkout: () => void;
  onViewHistory: () => void;
  onViewProfile: () => void;
  onBuildNextProgram?: () => void;
  onOpenFutureWorkout?: () => void;
  onOpenRebuildFlow?: () => void;
  onOpenRebuildAnalyzing?: () => void;
  onOpenRebuildRecommendation?: () => void;
  onOpenRebuildGenerating?: () => void;
  onOpenRebuildStartDate?: () => void;
  onViewProgramDetails?: () => void;
  onViewCompletedWorkout?: () => void;
  onViewSystemLoading?: () => void;
}
export function Dashboard({
  onStartWorkout,
  onViewHistory,
  onViewProfile,
  onBuildNextProgram,
  onOpenFutureWorkout,
  onOpenRebuildFlow,
  onOpenRebuildAnalyzing,
  onOpenRebuildRecommendation,
  onOpenRebuildGenerating,
  onOpenRebuildStartDate,
  onViewProgramDetails,
  onViewCompletedWorkout,
  onViewSystemLoading
}: DashboardProps) {
  const [showMissedWorkout, setShowMissedWorkout] = useState(true);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showCompletedProgram, setShowCompletedProgram] = useState(false);
  const [showCompletedReminder, setShowCompletedReminder] = useState(false);
  const [showRebuildSheet, setShowRebuildSheet] = useState(false);
  const [showPostWorkoutFeedback, setShowPostWorkoutFeedback] = useState(false);
  const [feedbackPreselected, setFeedbackPreselected] = useState<string | undefined>(undefined);
  const [feedbackShowSuccess, setFeedbackShowSuccess] = useState(false);
  const [showRecoveryFeedback, setShowRecoveryFeedback] = useState(false);
  const [recoveryPreselected, setRecoveryPreselected] = useState<string | undefined>(undefined);
  const [recoveryShowSuccess, setRecoveryShowSuccess] = useState(false);
  const [pendingRecoveryFeedback, setPendingRecoveryFeedback] = useState(false);
  // Debug panel state
  const [showDebug, setShowDebug] = useState(false);
  const [programCompletedByDuration, setProgramCompletedByDuration] = useState(false);
  // Calendar & scenario state
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [scenario, setScenario] = useState<DashboardScenario>('default');
  const [monthOffset, setMonthOffset] = useState(0);
  // Default week view data
  const defaultWeekDays = [{
    day: 'M',
    date: '12',
    active: false,
    hasWorkout: true,
    completed: true
  }, {
    day: 'T',
    date: '13',
    active: false,
    hasWorkout: false,
    completed: false
  }, {
    day: 'W',
    date: '14',
    active: true,
    hasWorkout: true,
    completed: false
  }, {
    day: 'T',
    date: '15',
    active: false,
    hasWorkout: false,
    completed: false
  }, {
    day: 'F',
    date: '16',
    active: false,
    hasWorkout: true,
    completed: false
  }, {
    day: 'S',
    date: '17',
    active: false,
    hasWorkout: false,
    completed: false
  }, {
    day: 'S',
    date: '18',
    active: false,
    hasWorkout: false,
    completed: false
  }];
  // Month calendar helpers
  const getMonthData = () => {
    // Base: March 2026
    const baseMonth = 2; // March (0-indexed)
    const baseYear = 2026;
    let month = baseMonth + monthOffset;
    let year = baseYear;
    while (month > 11) {
      month -= 12;
      year++;
    }
    while (month < 0) {
      month += 12;
      year--;
    }
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startDayOfWeek = firstDay.getDay(); // 0=Sun
    const adjustedStart = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // Mon=0
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return {
      year,
      month,
      daysInMonth,
      adjustedStart,
      monthName: monthNames[month]
    };
  };
  const isWorkoutDay = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    const dow = date.getDay(); // 0=Sun, 1=Mon, 4=Thu
    if (scenario === 'keep-date') {
      // Program starts March 26 (Thu). Training: Mon & Thu
      const programStart = new Date(2026, 2, 26);
      const programEnd = new Date(2026, 4, 20); // ~8 weeks
      if (date < programStart || date > programEnd) return false;
      return dow === 1 || dow === 4; // Mon or Thu
    }
    if (scenario === 'align-monday') {
      // Program starts March 30 (Mon). Training: Mon & Thu
      const programStart = new Date(2026, 2, 30);
      const programEnd = new Date(2026, 4, 24); // ~8 weeks
      if (date < programStart || date > programEnd) return false;
      return dow === 1 || dow === 4;
    }
    // Default: no month-level workout markers
    return false;
  };
  const isToday = (year: number, month: number, day: number): boolean => {
    if (scenario === 'keep-date') {
      return year === 2026 && month === 2 && day === 25; // March 25
    }
    if (scenario === 'align-monday') {
      return year === 2026 && month === 2 && day === 25;
    }
    return false;
  };
  const isProgramStart = (year: number, month: number, day: number): boolean => {
    if (scenario === 'keep-date') {
      return year === 2026 && month === 2 && day === 26;
    }
    if (scenario === 'align-monday') {
      return year === 2026 && month === 2 && day === 30;
    }
    return false;
  };
  const {
    year: calYear,
    month: calMonth,
    daysInMonth,
    adjustedStart,
    monthName
  } = getMonthData();
  const scenarioTitle = scenario === 'keep-date' ? 'Start: Thu, Mar 26' : scenario === 'align-monday' ? 'Start: Mon, Mar 30' : null;
  const scenarioSessionTitle = scenario === 'keep-date' ? 'Thursday, Mar 26' : scenario === 'align-monday' ? 'Monday, Mar 30' : 'Wednesday, Oct 14';
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-504">
      {/* Debug Toggle Button */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-505">
        <Bug size={14} data-id="element-506" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-60 text-xs animate-in fade-in zoom-in-95 duration-150 max-h-[70%] overflow-y-auto" data-id="element-507">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-508">
            Debug Panel
          </p>

          <button onClick={() => {
        setShowCompletedProgram(true);
        setShowDebug(false);
      }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-509">
            ▶ Open S024 Completed Program
          </button>

          <div className="border-t border-slate-700 pt-2 mt-1 mb-1" data-id="element-510">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-511">
              S07 Scenarios
            </p>

            <button onClick={() => {
          setScenario('keep-date');
          setCalendarView('month');
          setMonthOffset(0);
          setShowMissedWorkout(false);
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-512">
              ▶ Keep this start date
            </button>
            <button onClick={() => {
          setScenario('align-monday');
          setCalendarView('month');
          setMonthOffset(0);
          setShowMissedWorkout(false);
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-513">
              ▶ Align to next Monday
            </button>
            <button onClick={() => {
          setScenario('default');
          setCalendarView('week');
          setMonthOffset(0);
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-514">
              ▶ Reset to default
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-1 mb-1" data-id="element-515">
            <button onClick={() => {
          setShowDebug(false);
          if (onOpenFutureWorkout) onOpenFutureWorkout();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-516">
              ▶ Future workout + banner
            </button>
            <button onClick={() => {
          setShowDebug(false);
          if (onViewCompletedWorkout) onViewCompletedWorkout();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-517">
              ▶ Completed workout detail
            </button>
            <button onClick={() => {
          setShowDebug(false);
          if (onViewSystemLoading) onViewSystemLoading();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-518">
              ▶ S015 Loading / Blocking State
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-1 mb-1" data-id="element-519">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-520">
              S023 Rebuild Flow
            </p>
            <button onClick={() => {
          setShowDebug(false);
          setShowRebuildSheet(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-521">
              ▶ Open S023 Rebuild flow
            </button>
            <button onClick={() => {
          setShowDebug(false);
          if (onOpenRebuildAnalyzing) onOpenRebuildAnalyzing();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-522">
              ▶ S06 Analyzing after rebuild
            </button>
            <button onClick={() => {
          setShowDebug(false);
          if (onOpenRebuildRecommendation) onOpenRebuildRecommendation();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-523">
              ▶ S05 Recommendation after rebuild
            </button>
            <button onClick={() => {
          setShowDebug(false);
          if (onOpenRebuildGenerating) onOpenRebuildGenerating();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-524">
              ▶ S06 Generation after structure
            </button>
            <button onClick={() => {
          setShowDebug(false);
          if (onOpenRebuildStartDate) onOpenRebuildStartDate();
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-525">
              ▶ S07 Start date after rebuild
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-1 mb-1" data-id="element-526">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-527">
              Post-Workout Feedback
            </p>
            <button onClick={() => {
          setShowDebug(false);
          setFeedbackPreselected(undefined);
          setFeedbackShowSuccess(false);
          setShowPostWorkoutFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-528">
              ▶ Open post-workout feedback
            </button>
            <button onClick={() => {
          setShowDebug(false);
          setFeedbackPreselected('moderate');
          setFeedbackShowSuccess(false);
          setShowPostWorkoutFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-529">
              ▶ Feedback — option selected
            </button>
            <button onClick={() => {
          setShowDebug(false);
          setFeedbackPreselected('moderate');
          setFeedbackShowSuccess(true);
          setShowPostWorkoutFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-530">
              ▶ Feedback — success state
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-1 mb-1" data-id="element-531">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-532">
              Feedback Priority States
            </p>
            <button onClick={() => {
          setShowDebug(false);
          setFeedbackPreselected(undefined);
          setFeedbackShowSuccess(false);
          setPendingRecoveryFeedback(true);
          setShowPostWorkoutFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-533">
              ▶ Both pending (shows Post-Workout first)
            </button>
            <button onClick={() => {
          setShowDebug(false);
          setFeedbackPreselected(undefined);
          setFeedbackShowSuccess(false);
          setPendingRecoveryFeedback(false);
          setShowPostWorkoutFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-534">
              ▶ Shown 2 days later (still pending)
            </button>
            <button onClick={() => {
          setShowDebug(false);
          setRecoveryPreselected(undefined);
          setRecoveryShowSuccess(false);
          setPendingRecoveryFeedback(false);
          setShowRecoveryFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-535">
              ▶ Recovery (after Post-Workout done)
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-1 mb-1" data-id="element-536">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-537">
              Feedback Success States
            </p>
            <button onClick={() => {
          setShowDebug(false);
          setFeedbackPreselected('moderate');
          setFeedbackShowSuccess(true);
          setShowPostWorkoutFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-538">
              ▶ Post-Workout — success state
            </button>
            <button onClick={() => {
          setShowDebug(false);
          setRecoveryPreselected('mostly_recovered');
          setRecoveryShowSuccess(true);
          setShowRecoveryFeedback(true);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-539">
              ▶ Recovery — success state
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-1 space-y-2" data-id="element-540">
            <div className="flex items-center justify-between px-2" data-id="element-541">
              <span className="text-slate-300 font-medium" data-id="element-542">
                program_completed
              </span>
              <button onClick={() => {
            const next = !programCompletedByDuration;
            setProgramCompletedByDuration(next);
            if (next) {
              setShowCompletedProgram(true);
            } else {
              setShowCompletedProgram(false);
              setShowCompletedReminder(false);
            }
          }} className={`w-9 h-5 rounded-full transition-colors relative ${programCompletedByDuration ? 'bg-emerald-500' : 'bg-slate-600'}`} data-id="element-543">
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${programCompletedByDuration ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-544" />
              </button>
            </div>
          </div>
        </div>}

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pt-12 pb-28" data-id="element-545">
        {/* Header */}
        <div className="flex items-center justify-between mb-8" data-id="element-546">
          <div data-id="element-547">
            <p className="text-slate-500 font-bold text-sm uppercase tracking-wider mb-1" data-id="element-548">
              {scenario !== 'default' ? 'Tuesday, Mar 25, 2026' : 'Wednesday, Oct 14'}
            </p>
            <h1 className="text-3xl font-extrabold text-slate-800" data-id="element-549">
              Hi, Flex! 👋
            </h1>
          </div>
          <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200 shrink-0" data-id="element-550">
            <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-551" />
          </div>
        </div>

        {/* Calendar Section */}
        {scenario === 'default' /* Default Week View */ ? <div className="flex justify-between items-center mb-8 bg-white p-4 rounded-3xl shadow-sm border border-slate-100" data-id="element-552">
            {defaultWeekDays.map((d, i) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-553">
                <span className="text-xs font-bold text-slate-400" data-id="element-554">
                  {d.day}
                </span>
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold relative
                  ${d.active ? 'bg-emerald-500 text-white shadow-md' : 'text-slate-700'}
                `} data-id="element-555">
                  {d.date}
                  {d.hasWorkout && !d.completed && !d.active && <div className="absolute -bottom-1 w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-556" />}
                  {d.completed && <div className="absolute -bottom-1 w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-557" />}
                </div>
              </div>)}
          </div> /* Month/Week Calendar with Toggle */ : <div className="mb-8 bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden" data-id="element-558">
            {/* View Toggle */}
            <div className="flex items-center justify-between p-4 pb-2" data-id="element-559">
              <div className="flex bg-stone-100 rounded-xl p-0.5" data-id="element-560">
                <button onClick={() => setCalendarView('month')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${calendarView === 'month' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`} data-id="element-561">
                  Month
                </button>
                <button onClick={() => setCalendarView('week')} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${calendarView === 'week' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400'}`} data-id="element-562">
                  Week
                </button>
              </div>
              {scenarioTitle && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg uppercase tracking-wider" data-id="element-563">
                  {scenarioTitle}
                </span>}
            </div>

            {calendarView === 'month' ? <div className="px-4 pb-4" data-id="element-564">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-3" data-id="element-565">
                  <button onClick={() => setMonthOffset(monthOffset - 1)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-566">
                    <ChevronLeft size={18} data-id="element-567" />
                  </button>
                  <span className="text-sm font-extrabold text-slate-800" data-id="element-568">
                    {monthName} {calYear}
                  </span>
                  <button onClick={() => setMonthOffset(monthOffset + 1)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-569">
                    <ChevronRight size={18} data-id="element-570" />
                  </button>
                </div>

                {/* Day Headers */}
                <div className="grid grid-cols-7 gap-1 mb-1" data-id="element-571">
                  {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <div key={i} className="text-center text-[10px] font-bold text-slate-400 py-1" data-id="element-572">
                      {d}
                    </div>)}
                </div>

                {/* Day Grid */}
                <div className="grid grid-cols-7 gap-1" data-id="element-573">
                  {Array.from({
              length: adjustedStart
            }).map((_, i) => <div key={`empty-${i}`} className="h-9" data-id="element-574" />)}
                  {Array.from({
              length: daysInMonth
            }).map((_, i) => {
              const day = i + 1;
              const workout = isWorkoutDay(calYear, calMonth, day);
              const today = isToday(calYear, calMonth, day);
              const programStart = isProgramStart(calYear, calMonth, day);
              return <button key={day} onClick={() => {
                if (workout && !programStart && onOpenFutureWorkout) {
                  onOpenFutureWorkout();
                } else if (workout || programStart) {
                  onStartWorkout();
                }
              }} className={`
                          h-9 rounded-xl flex items-center justify-center text-xs font-bold relative transition-colors
                          ${programStart ? 'bg-emerald-500 text-white shadow-sm' : ''}
                          ${workout && !programStart ? 'bg-emerald-50 text-emerald-700' : ''}
                          ${today && !workout && !programStart ? 'ring-2 ring-slate-300 text-slate-800' : ''}
                          ${!workout && !today && !programStart ? 'text-slate-600' : ''}
                        `} data-id="element-575">
                        {day}
                        {programStart && <div className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-white" data-id="element-576" />}
                      </button>;
            })}
                </div>

                {/* Legend */}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100" data-id="element-577">
                  <div className="flex items-center gap-1.5" data-id="element-578">
                    <div className="w-3 h-3 rounded bg-emerald-500" data-id="element-579" />
                    <span className="text-[10px] font-bold text-slate-400" data-id="element-580">
                      Program start
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-581">
                    <div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" data-id="element-582" />
                    <span className="text-[10px] font-bold text-slate-400" data-id="element-583">
                      Workout day
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-584">
                    <div className="w-3 h-3 rounded ring-2 ring-slate-300" data-id="element-585" />
                    <span className="text-[10px] font-bold text-slate-400" data-id="element-586">
                      Today
                    </span>
                  </div>
                </div>
              </div> /* Week View for scenario modes */ : <div className="px-4 pb-4" data-id="element-587">
                <div className="flex justify-between items-center" data-id="element-588">
                  {(() => {
              const weekStart = scenario === 'keep-date' ? [{
                day: 'M',
                date: '23',
                workout: false
              }, {
                day: 'T',
                date: '24',
                workout: false
              }, {
                day: 'W',
                date: '25',
                workout: false,
                today: true
              }, {
                day: 'T',
                date: '26',
                workout: true,
                programStart: true
              }, {
                day: 'F',
                date: '27',
                workout: false
              }, {
                day: 'S',
                date: '28',
                workout: false
              }, {
                day: 'S',
                date: '29',
                workout: false
              }] : [{
                day: 'M',
                date: '30',
                workout: true,
                programStart: true
              }, {
                day: 'T',
                date: '31',
                workout: false
              }, {
                day: 'W',
                date: '1',
                workout: false
              }, {
                day: 'T',
                date: '2',
                workout: true
              }, {
                day: 'F',
                date: '3',
                workout: false
              }, {
                day: 'S',
                date: '4',
                workout: false
              }, {
                day: 'S',
                date: '5',
                workout: false
              }];
              return weekStart.map((d: any, i: number) => <div key={i} className="flex flex-col items-center gap-2" data-id="element-589">
                        <span className="text-xs font-bold text-slate-400" data-id="element-590">
                          {d.day}
                        </span>
                        <div className={`
                          w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold relative
                          ${d.programStart ? 'bg-emerald-500 text-white shadow-md' : ''}
                          ${d.workout && !d.programStart ? 'bg-emerald-50 text-emerald-700' : ''}
                          ${d.today ? 'ring-2 ring-slate-300' : ''}
                          ${!d.workout && !d.today && !d.programStart ? 'text-slate-700' : ''}
                        `} data-id="element-591">
                          {d.date}
                          {d.workout && !d.programStart && <div className="absolute -bottom-1 w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-592" />}
                        </div>
                      </div>);
            })()}
                </div>
              </div>}
          </div>}

        {/* Today's Workout Card */}
        <div className="mb-6" data-id="element-593">
          <h2 className="text-lg font-extrabold text-slate-800 mb-3" data-id="element-594">
            {scenario !== 'default' ? 'Next Session' : "Today's Session"}
          </h2>
          <div className="bg-slate-800 rounded-[2rem] p-6 text-white relative overflow-hidden shadow-lg" data-id="element-595">
            <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/20 rounded-bl-full blur-2xl -z-0" data-id="element-596" />

            <div className="relative z-10" data-id="element-597">
              <div className="flex items-center gap-2 mb-2" data-id="element-598">
                <Flame size={16} className="text-emerald-400" data-id="element-599" />
                <span className="text-sm font-bold text-emerald-400 uppercase tracking-wider" data-id="element-600">
                  {scenario !== 'default' ? 'Week 1 • Day 1' : 'Day 2 of 3'}
                </span>
              </div>
              <h3 className="text-2xl font-extrabold mb-1" data-id="element-601">Full Body Power</h3>
              <p className="text-slate-300 font-medium mb-1" data-id="element-602">
                45 mins • 6 exercises
              </p>
              <p className="text-slate-400 font-medium text-sm mb-5" data-id="element-603">
                {scenarioSessionTitle}
              </p>

              <button onClick={onStartWorkout} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-4 px-6 font-bold flex items-center justify-center gap-2 transition-colors shadow-md" data-id="element-604">
                <Play size={20} fill="currentColor" data-id="element-605" />
                Start Workout
              </button>
            </div>
          </div>
        </div>

        {/* Active Program Summary */}
        <div data-id="element-606">
          <h2 className="text-lg font-extrabold text-slate-800 mb-3" data-id="element-607">
            Current Program
          </h2>

          {showCompletedReminder && <div className="mb-4 bg-emerald-50 border-2 border-emerald-200 rounded-3xl p-5 flex items-center justify-between animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-608">
              <div className="flex items-center gap-3" data-id="element-609">
                <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center shrink-0" data-id="element-610">
                  <Trophy size={20} data-id="element-611" />
                </div>
                <div data-id="element-612">
                  <h4 className="font-extrabold text-slate-800 text-sm" data-id="element-613">
                    Program completed
                  </h4>
                </div>
              </div>
              <button onClick={() => {
            setShowCompletedReminder(false);
            if (onBuildNextProgram) onBuildNextProgram();
          }} className="text-sm font-bold text-emerald-600 hover:text-emerald-700 transition-colors whitespace-nowrap ml-3" data-id="element-614">
                Build next →
              </button>
            </div>}

          <button onClick={onViewProgramDetails} className="w-full bg-white rounded-3xl p-5 flex items-center justify-between border border-slate-100 shadow-sm hover:border-slate-200 transition-colors text-left" data-id="element-615">
            <div className="flex items-center gap-4" data-id="element-616">
              <div className="w-12 h-12 bg-pink-100 text-pink-500 rounded-2xl flex items-center justify-center shrink-0" data-id="element-617">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-618">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" data-id="element-619" />
                </svg>
              </div>
              <div data-id="element-620">
                <h4 className="font-extrabold text-slate-800" data-id="element-621">
                  8-Week Full Body
                </h4>
                <p className="text-sm font-medium text-slate-500" data-id="element-622">
                  {scenario !== 'default' ? 'Week 1 • Mon & Thu' : 'Week 1 • Muscle Building'}
                </p>
              </div>
            </div>
            <ChevronRight className="text-slate-400" data-id="element-623" />
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-4 pb-8 flex justify-around items-center z-30" data-id="element-624">
        <button className="flex flex-col items-center gap-1 text-emerald-500" data-id="element-625">
          <Home size={24} strokeWidth={2.5} data-id="element-626" />
          <span className="text-[10px] font-bold" data-id="element-627">Home</span>
        </button>
        <button onClick={onViewHistory} className="flex flex-col items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-628">
          <History size={24} strokeWidth={2.5} data-id="element-629" />
          <span className="text-[10px] font-bold" data-id="element-630">History</span>
        </button>
        <button onClick={onViewProfile} className="flex flex-col items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors" data-id="element-631">
          <User size={24} strokeWidth={2.5} data-id="element-632" />
          <span className="text-[10px] font-bold" data-id="element-633">Profile</span>
        </button>
      </div>

      {/* Modals */}
      {showMissedWorkout && <MissedWorkoutModal date="Monday, Oct 12" onOpenWorkout={() => {
      setShowMissedWorkout(false);
      onStartWorkout();
    }} onReschedule={() => {
      setShowMissedWorkout(false);
      setShowRescheduleModal(true);
    }} onCancelWorkout={() => {
      setShowMissedWorkout(false);
      setShowSkipModal(true);
    }} onClose={() => setShowMissedWorkout(false)} data-id="element-634" />}

      {showSkipModal && <SkipWorkoutModal onClose={() => setShowSkipModal(false)} data-id="element-635" />}

      {showRescheduleModal && <ChangeTargetDateModal helperText="Choose a new date for this workout. It can be outside your usual training routine if needed." onConfirm={() => {
      setShowRescheduleModal(false);
    }} onCancel={() => setShowRescheduleModal(false)} data-id="element-636" />}

      {showCompletedProgram && <CompletedProgramModal onBuildNext={() => {
      setShowCompletedProgram(false);
      if (onBuildNextProgram) onBuildNextProgram();
    }} onLater={() => {
      setShowCompletedProgram(false);
      setShowCompletedReminder(true);
    }} data-id="element-637" />}

      {showRebuildSheet && <RebuildProgramSheet onNext={() => {
      setShowRebuildSheet(false);
      if (onOpenRebuildAnalyzing) onOpenRebuildAnalyzing();
    }} onClose={() => setShowRebuildSheet(false)} data-id="element-638" />}

      {showPostWorkoutFeedback && <PostWorkoutFeedbackSheet onSubmit={feedback => {
      console.log('Workout feedback:', feedback);
      setShowPostWorkoutFeedback(false);
      if (pendingRecoveryFeedback) {
        setTimeout(() => {
          setShowRecoveryFeedback(true);
          setPendingRecoveryFeedback(false);
        }, 300);
      }
    }} onSkip={() => {
      setShowPostWorkoutFeedback(false);
      if (pendingRecoveryFeedback) {
        setTimeout(() => {
          setShowRecoveryFeedback(true);
          setPendingRecoveryFeedback(false);
        }, 300);
      }
    }} onClose={() => {
      setShowPostWorkoutFeedback(false);
      if (pendingRecoveryFeedback) {
        setTimeout(() => {
          setShowRecoveryFeedback(true);
          setPendingRecoveryFeedback(false);
        }, 300);
      }
    }} preselectedOption={feedbackPreselected} showSuccess={feedbackShowSuccess} data-id="element-639" />}

      {showRecoveryFeedback && <RecoveryFeedbackSheet onSubmit={feedback => {
      console.log('Recovery feedback:', feedback);
      setShowRecoveryFeedback(false);
    }} onSkip={() => setShowRecoveryFeedback(false)} onClose={() => setShowRecoveryFeedback(false)} preselectedOption={recoveryPreselected} showSuccess={recoveryShowSuccess} data-id="element-640" />}
    </div>;
}